import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { formatCalendarDate } from '../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  SCOUT_LABELS,
  SCOUT_LABEL_TEXT,
  count,
  ratePct,
  scoutRuleLabel,
  scoutStatusLabel,
} from '../../lib/scout-agent';
import type { ScoutLabel, ScoutMetrics, ScoutNudgeType, ScoutShadowNudge, ScoutShadowReport } from '../../types/scout';

/**
 * Scout's scorecard and shadow report (api#1080), on Settings → Scout below
 * the mode — the Billing agent page's metrics + shadow sections, for Scout.
 *
 * WHAT IT IS FOR: FieldIQ marks a Scout rule proven (admin.field-iq.ai) only
 * once its precision is measured, and precision is right / (right + wrong) over
 * nudges a reviewer here has LABELLED. So the shadow report is a labelling
 * screen: every nudge in the window, shadow included, each with Right / Wrong /
 * Don't care. A tap on the chosen verdict clears it.
 *
 * EVERY FIGURE IS THE API'S. The window is the API's default (the last 30
 * tenant-local days) and its days are shown as calendar days. A rate is the
 * API's 0–1 value rounded for display; `null` is `—`, never 0% — the API
 * withholds precision below `minLabelled` labels (the small-N rule), and the
 * card says how many it needs rather than showing a number that proves nothing.
 *
 * Admins and office staff, as the API's routes are; both may label.
 */

export const SCOUT_METRICS_KEY = ['scout', 'metrics'] as const;
export const SCOUT_SHADOW_KEY = ['scout', 'shadow-report'] as const;

const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted';
const NUDGE_TYPES: ScoutNudgeType[] = ['follow_up_overdue', 'follow_up_due', 'customer_quiet'];

function day(v: string | null | undefined): string {
  return formatCalendarDate(v) ?? '—';
}

function windowLine(w: { from: string; to: string; timeZone: string } | undefined): string {
  return w ? `${day(w.from)} – ${day(w.to)}${w.timeZone ? ` (${w.timeZone})` : ''}` : '—';
}

// ─── Scorecard ───────────────────────────────────────────────────────────────

export function ScoutMetricsSection() {
  const query = useQuery({
    queryKey: SCOUT_METRICS_KEY,
    queryFn: async () => unwrap<ScoutMetrics>((await api.scout.metrics()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const m = query.data;

  return (
    <section aria-labelledby="scout-metrics" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="scout-metrics">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Last 30 days</p>
        <h2 id="scout-metrics" className="text-base font-semibold text-white">How Scout is doing</h2>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="Scout's scorecard" allowed="admins and office staff" />
        </div>
      ) : !m ? (
        <div className="px-4 pb-4"><span role="status" aria-label="Loading" className="inline-block h-4 w-20 animate-pulse rounded bg-fieldiq-border" /></div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" aria-label="Nudge precision">
              <thead>
                <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Nudge</th>
                  <th scope="col" className="px-3 py-2 font-medium">Raised</th>
                  <th scope="col" className="px-3 py-2 font-medium">Right / Wrong / Don't care</th>
                  <th scope="col" className="px-3 py-2 font-medium">Precision</th>
                  <th scope="col" className="px-3 py-2 font-medium">Acted / Dismissed</th>
                </tr>
              </thead>
              <tbody>
                {NUDGE_TYPES.map((t) => {
                  const n = m.nudges?.[t];
                  return (
                    <tr key={t} className="border-b border-fieldiq-border last:border-b-0 align-top" data-testid={`scout-metric-${t}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium text-fieldiq-text">{scoutRuleLabel(t)}</th>
                      <td className="px-3 py-2 tabular-nums text-fieldiq-text">{count(n?.raised)}</td>
                      <td className="px-3 py-2 tabular-nums text-fieldiq-text">{count(n?.right)} / {count(n?.wrong)} / {count(n?.dontCare)}</td>
                      <td className="px-3 py-2" data-testid={`scout-precision-${t}`}>
                        <span className="tabular-nums text-fieldiq-text">{ratePct(n?.precision)}</span>
                        {n && n.precision === null && (
                          <span className="block text-xs text-fieldiq-muted">
                            needs {count(m.minLabelled)} right-or-wrong labels
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-fieldiq-text">{count(n?.acted)} / {count(n?.dismissed)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <dl className="grid gap-3 border-t border-fieldiq-border px-4 py-3 text-sm sm:grid-cols-2">
            <div data-testid="scout-metric-brief">
              <dt className={EYEBROW}>Pre-visit briefs</dt>
              <dd className="mt-1 text-fieldiq-text tabular-nums">
                {count(m.customerBrief?.briefs)} given · {count(m.customerBrief?.grounded)} of {count(m.customerBrief?.scored)} scored replies grounded ({ratePct(m.customerBrief?.groundedRate)})
              </dd>
            </div>
            <div data-testid="scout-metric-meeting-log">
              <dt className={EYEBROW}>Meetings logged by Scout</dt>
              <dd className="mt-1 text-fieldiq-text tabular-nums">
                {count(m.meetingLog?.saved)} saved · {count(m.meetingLog?.acceptedAsDrafted)} accepted as first drafted ({ratePct(m.meetingLog?.acceptedRate)})
              </dd>
            </div>
          </dl>
          <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted" data-testid="scout-metrics-window">
            {windowLine(m.window)}
          </p>
        </>
      )}
    </section>
  );
}

// ─── Shadow report: label each nudge ─────────────────────────────────────────

/** Exhaustive over ScoutLabel. A pressed verdict is filled; the rest are outlines. */
const LABEL_FILL: Record<ScoutLabel, string> = {
  right: 'bg-fi-signal-green',
  wrong: 'bg-fi-signal-red',
  dont_care: 'bg-fi-signal-slate',
};

function LabelButtons({ nudge, onLabel, busy }: { nudge: ScoutShadowNudge; onLabel: (label: ScoutLabel | null) => void; busy: boolean }) {
  return (
    <div role="group" aria-label={`Label for: ${nudge.message}`} className="flex flex-wrap gap-1.5">
      {SCOUT_LABELS.map((l) => {
        const pressed = nudge.label === l;
        return (
          <button
            key={l}
            type="button"
            aria-pressed={pressed}
            disabled={busy}
            onClick={() => onLabel(pressed ? null : l)}
            className={`min-h-[36px] rounded-fi-pill border px-3 text-xs font-bold disabled:opacity-50 ${
              pressed
                ? `${LABEL_FILL[l]} border-black/20 text-fi-signal-ink`
                : 'border-fieldiq-border bg-fieldiq-dark text-fieldiq-text hover:border-fieldiq-accent'
            }`}
          >
            {SCOUT_LABEL_TEXT[l]}
          </button>
        );
      })}
    </div>
  );
}

export function ScoutShadowSection() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: SCOUT_SHADOW_KEY,
    queryFn: async () => unwrap<ScoutShadowReport>((await api.scout.shadowReport()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const report = query.data;

  const label = useMutation({
    mutationFn: ({ id, value }: { id: string; value: ScoutLabel | null }) => api.scout.label(id, value),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not save the label'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SCOUT_SHADOW_KEY });
      queryClient.invalidateQueries({ queryKey: SCOUT_METRICS_KEY });
    },
  });

  const rows = Array.isArray(report?.nudges) ? report!.nudges : null;

  return (
    <section aria-labelledby="scout-shadow" className={PANEL} data-testid="scout-shadow">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Shadow report</p>
        <h2 id="scout-shadow" className="text-base font-semibold text-white">Was Scout right?</h2>
        <p className="mt-1 text-xs text-fieldiq-muted">
          Every nudge Scout raised, including the ones no rep saw. Your labels are how FieldIQ decides when a nudge is ready for reps.
        </p>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the shadow report" allowed="admins and office staff" />
        </div>
      ) : !rows ? (
        <div className="px-4 pb-4"><span role="status" aria-label="Loading" className="inline-block h-4 w-20 animate-pulse rounded bg-fieldiq-border" /></div>
      ) : rows.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-fieldiq-muted" data-testid="scout-shadow-empty">Scout raised no nudges in this window.</p>
      ) : (
        <ul className="divide-y divide-fieldiq-border border-t border-fieldiq-border" aria-label="Scout nudges">
          {rows.map((n) => (
            <li key={n.id} className="px-4 py-3" data-testid={`scout-nudge-${n.id}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fieldiq-muted">
                <span className="font-semibold text-fieldiq-text">{scoutRuleLabel(n.type)}</span>
                <span>{day(n.nudgeDate)}</span>
                <span>{n.repName ?? '—'}</span>
                <span>{scoutStatusLabel(n.status)}</span>
              </div>
              <Link to={`/customers/${n.customerId}`} className="mt-1 block text-sm text-white hover:underline">
                {n.message || '—'}
              </Link>
              {n.dismissReason && <p className="mt-0.5 text-xs text-fieldiq-muted">Dismissed: {n.dismissReason}</p>}
              <div className="mt-2">
                <LabelButtons
                  nudge={n}
                  busy={label.isPending && label.variables?.id === n.id}
                  onLabel={(value) => label.mutate({ id: n.id, value })}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      {report?.window && (
        <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
          {rows ? `${rows.length} ${rows.length === 1 ? 'nudge' : 'nudges'}` : '—'} · {windowLine(report.window)}
        </p>
      )}
    </section>
  );
}
